import { Link } from 'react-router-dom';
import styled from 'styled-components';

export const HeaderContainer = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: ${({ $scrolled }) => $scrolled ? 'rgba(10, 22, 40, 0.95)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(12px)' : 'none'};
  box-shadow: ${({ $scrolled }) => $scrolled ? '0 2px 20px rgba(0, 0, 0, 0.3)' : 'none'};
  border-bottom: 1px solid ${({ $scrolled }) => $scrolled ? 'var(--border-color)' : 'transparent'};
  transition: all var(--transition-base);
`;

export const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  max-width: var(--container-max);
  margin: 0 auto;
`;

export const Logo = styled(Link)`
  font-family: var(--font-heading);
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: 3px;
  color: var(--text-primary);
  text-transform: uppercase;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--accent-teal);
  }
`;

export const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

export const NavLinks = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 75%;
    max-width: 320px;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 5rem;
    background-color: var(--bg-secondary);
    border-left: 1px solid var(--border-color);
    box-shadow: var(--shadow-lg);
    transform: translateX(${({ $isOpen }) => $isOpen ? '0' : '100%'});
    transition: transform var(--transition-base);
    z-index: 1000;
  }
`;

export const NavItem = styled(Link)`
  color: var(--text-primary);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-left: 2rem;
  transition: color var(--transition-fast);
  position: relative;

  &:hover {
    color: var(--accent-teal);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--gradient-accent);
    border-radius: 1px;
    transition: width var(--transition-base);
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    margin: 1.2rem 0;
    font-size: 1rem;

    &::after {
      display: none;
    }
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

export const BookButton = styled(Link)`
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 0.6rem 1.5rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-left: 2rem;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(244, 170, 0, 0.35);
    color: var(--text-inverse);
  }

  @media (max-width: 768px) {
    margin: 1.5rem 0;
    padding: 0.8rem 2rem;
  }
`;

export const HeaderSocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1.5rem;

  @media (max-width: 768px) {
    margin: 1rem 0 0;
    justify-content: center;
  }
`;

export const HeaderSocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: var(--text-secondary);
  transition: all var(--transition-fast);

  &:hover {
    color: var(--accent-teal);
    transform: translateY(-1px);
  }
`;

/* ─── Charters Dropdown ─── */

export const DropdownWrapper = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

export const ChartersTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

export const ChevronIcon = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 0.6rem;
  color: var(--text-secondary);
  transition: transform var(--transition-fast);
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});

  &.desktop-only {
    display: inline-flex;
    @media (max-width: 768px) {
      display: none;
    }
  }

  &.mobile-only {
    display: none;
    @media (max-width: 768px) {
      display: inline-flex;
      margin-top: -0.8rem;
    }
  }
`;

export const DropdownPanel = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2.5rem;
  min-width: 420px;
  padding: 1.5rem 2rem;
  background: rgba(10, 22, 40, 0.98);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-color);
  border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transform: translateX(-50%) translateY(${({ $visible }) => ($visible ? '0' : '-8px')});
  transition: opacity var(--transition-base), transform var(--transition-base), visibility var(--transition-base);
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  /* invisible bridge to prevent gap hover-out */
  &::before {
    content: '';
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const DropdownColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 160px;
`;

export const DropdownHeader = styled(Link)`
  color: var(--accent-teal);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--accent-gold);
  }
`;

export const DropdownLink = styled(Link)`
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 400;
  padding: 0.3rem 0;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--accent-teal);
  }
`;

/* ─── Mobile Accordion Sub-Nav ─── */

export const MobileSubNav = styled.div<{ $expanded: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    max-height: ${({ $expanded }) => ($expanded ? '500px' : '0')};
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition: max-height var(--transition-base), opacity var(--transition-base);
  }
`;

export const MobileSubLink = styled(Link)`
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 0.85rem;
  padding: 0.5rem 0;
  padding-left: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--accent-teal);
  }
`;

export const MobileSubHeader = styled(Link)`
  color: var(--accent-teal);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.7rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 0.6rem 0 0.2rem;
  padding-left: 1rem;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--accent-gold);
  }
`;
